import classNames from 'classnames';

import Select from 'react-select';

import { selectStyles } from '../../../constants/select';
import type { FieldChangeEvent } from '../../../types';

import InputError from '../inputError';

export interface SelectOption {
  label: string;
  value: string | number;
}

interface Props {
  label: string;
  name: string;
  options: SelectOption[];
  placeholder?: string;
  hideLabel?: boolean;
  // The stored field value is the option's `value` (a string before anything
  // has been picked), not the option object, so resolve it against `options`
  // before handing it to react-select.
  value?: SelectOption | string;
  error?: boolean;
  errorMsg?: string;
  onChange?: (event: FieldChangeEvent<SelectOption['value']>) => void;
}

const SearchableSelect = ({
  label,
  placeholder,
  hideLabel = true,
  name,
  options,
  value = '',
  error = false,
  errorMsg = '',
  onChange = () => null,
  ...props
}: Props) => {
  const buildEvent = (eventValue: SelectOption['value']) => {
    return {
      currentTarget: {
        name,
        value: eventValue,
        type: 'select',
      },
    };
  };

  const onChangeOption = (selectedOption: SelectOption | null) => {
    // Clearing the select emits '' — the same "nothing picked" value the
    // field starts with.
    const event = buildEvent(selectedOption ? selectedOption.value : '');
    onChange(event);
  };

  const classes = classNames({
    field: true,
    'field--searchable-select': true,
    'field--hidden-label': hideLabel,
    'field--has-error': !!error,
  });

  return (
    <div className={classes} data-testid="searchableSelect">
      <label className="field__label" htmlFor={name}>
        {label}
      </label>
      <Select
        placeholder={placeholder}
        name={name}
        inputId={name}
        options={options}
        isClearable
        isSearchable
        maxMenuHeight={180}
        styles={selectStyles}
        classNamePrefix={name}
        defaultValue={typeof value === 'object' ? value : null}
        onChange={onChangeOption}
        {...props}
      />
      {error && <InputError errorMsg={errorMsg} />}
    </div>
  );
};

export default SearchableSelect;
